import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';

// Internet (NBN + Supa) services grouped by state or POP / POI. No pricing, so it is open to all staff;
// the cost and margin view is the admin-only Internet Financial report.

const NO_VALUE = '__none__';

type GroupBy = 'state' | 'pop';
type FacetKey = 'state' | 'plan' | 'technology' | 'pop';

interface Row {
  id: string;
  service_id: string;
  service_type: string;
  service_type_label: string;
  status: string;
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  plan_name: string | null;
  speed_plan: string | null;
  technology_type: string | null;
  pop: string | null;
  state: string | null;
  address: string | null;
}

interface Group {
  key: string;
  label: string;
  count: number;
  by_type: Record<string, number>;
  rows: Row[];
}

interface Facet {
  options: { value: string; label: string; count: number }[];
  none_count: number;
}

interface Report {
  group_by: GroupBy;
  groups: Group[];
  totals: { services: number; by_type: Record<string, number> };
  facets: Record<FacetKey, Facet>;
  service_types: { value: string; label: string }[];
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All statuses' },
];

const FACETS: { key: FacetKey; label: string; allLabel: string; noneLabel: string; minWidth: number }[] = [
  { key: 'state', label: 'State', allLabel: 'All states', noneLabel: 'Unknown state', minWidth: 150 },
  { key: 'pop', label: 'POP / POI', allLabel: 'All POPs / POIs', noneLabel: 'No POP / POI', minWidth: 200 },
  { key: 'plan', label: 'Plan', allLabel: 'All plans', noneLabel: 'No plan found', minWidth: 200 },
  { key: 'technology', label: 'Technology', allLabel: 'All technologies', noneLabel: 'No technology', minWidth: 170 },
];

const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

// Spreadsheet apps run text starting with = + - @ as a formula; neutralise user-supplied text
const csvText = (v: string | null | undefined) => {
  const s = (v ?? '').toString();
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

function buildCsv(report: Report): string {
  const header = ['State', 'POP / POI', 'Service ID', 'Type', 'Account', 'Account number', 'Plan', 'Technology', 'Status', 'Address'];
  const lines = [header.map((h) => `"${h}"`).join(',')];
  for (const g of report.groups) {
    for (const r of g.rows) {
      lines.push([
        csvText(r.state || 'Unknown'), csvText(r.pop), csvText(r.service_id), csvText(r.service_type_label), csvText(r.account_name),
        csvText(r.account_number), csvText(r.plan_name || r.speed_plan), csvText(r.technology_type), csvText(r.status), csvText(r.address),
      ].join(','));
    }
  }
  return lines.join('\r\n');
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const SummaryTile: React.FC<{ label: string; value: string; sub?: string; onClick?: () => void; active?: boolean }> = ({ label, value, sub, onClick, active }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2, flex: '1 1 130px', minWidth: 130,
      cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? 'primary.main' : undefined,
      '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

const InternetServicesReportPage: React.FC = () => {
  const [groupBy, setGroupBy] = useState<GroupBy>('state');
  const [serviceType, setServiceType] = useState('all');
  const [status, setStatus] = useState('active');
  const [selected, setSelected] = useState<Partial<Record<FacetKey, string>>>({});
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: Report = await apiService.getInternetServicesReport({ group_by: groupBy, service_type: serviceType, status, ...selected });
      setReport(data);
      // A selection that no longer exists under the other filters would give an empty report: reset it
      const stale = FACETS.map((f) => f.key).filter((k) => {
        const v = selected[k];
        const f = data.facets[k];
        return !!v && !!f && !(v === NO_VALUE ? f.none_count > 0 : f.options.some((o) => o.value === v));
      });
      if (stale.length) setSelected((cur) => Object.fromEntries(Object.entries(cur).filter(([k]) => !stale.includes(k as FacetKey))));
    } catch (e) {
      setError(apiMessage(e, 'Could not load the report.'));
    } finally {
      setLoading(false);
    }
  }, [groupBy, serviceType, status, selected]);

  useEffect(() => { load(); }, [load]);

  const filtersActive = FACETS.some((f) => !!selected[f.key]);
  const typeLabel = (code: string) => report?.service_types.find((t) => t.value === code)?.label || code;
  const groupFacet: FacetKey = groupBy === 'state' ? 'state' : 'pop';
  const total = report?.totals.services ?? 0;

  const exportCsv = () => {
    if (!report) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`internet-services-by-${groupBy}-${status}${filtersActive ? '-filtered' : ''}-${stamp}.csv`, buildCsv(report));
  };

  const renderRow = (r: Row) => (
    <TableRow key={r.id} hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.service_id}</TableCell>
      <TableCell>{r.service_type_label}</TableCell>
      <TableCell>
        <Box component={RouterLink} to={`/accounts/${r.account_id}`} sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
          {r.account_name || '—'}
        </Box>
        {r.account_number && <Typography variant="caption" color="text.secondary" display="block">{r.account_number}</Typography>}
      </TableCell>
      <TableCell>{r.plan_name || (r.speed_plan ? <Typography variant="body2" color="text.secondary">{r.speed_plan}</Typography> : '—')}</TableCell>
      <TableCell>{r.technology_type || '—'}</TableCell>
      {groupBy === 'state' ? <TableCell>{r.pop || '—'}</TableCell> : <TableCell>{r.state || 'Unknown'}</TableCell>}
      <TableCell sx={{ minWidth: 220 }}>{r.address || '—'}</TableCell>
      <TableCell>
        <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} variant="outlined" sx={{ textTransform: 'capitalize' }} />
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>Internet Services</Typography>
          <Typography variant="body2" color="text.secondary">
            NBN and Supa Networks services by {groupBy === 'state' ? 'state' : 'POP / POI'}. State comes from the service address, then the account address.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!report || loading}>Export CSV</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, v: GroupBy | null) => v && setGroupBy(v)} aria-label="Group by">
          <ToggleButton value="state">By state</ToggleButton>
          <ToggleButton value="pop">By POP / POI</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" label="Service type" value={serviceType} onChange={(e) => setServiceType(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="all">All internet services</MenuItem>
          {(report?.service_types || [{ value: 'nbn', label: 'NBN Co' }, { value: 'supa_networks', label: 'Supa Networks' }]).map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {FACETS.map((def) => {
          const facet = report?.facets[def.key];
          const value = selected[def.key] || '';
          return (
            <TextField
              key={def.key}
              select
              size="small"
              label={def.label}
              value={value}
              onChange={(e) => setSelected((cur) => ({ ...cur, [def.key]: e.target.value }))}
              sx={{ minWidth: def.minWidth }}
            >
              <MenuItem value="">{def.allLabel}</MenuItem>
              {(facet?.options || []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label} ({o.count})</MenuItem>)}
              {((facet?.none_count ?? 0) > 0 || value === NO_VALUE) && <MenuItem value={NO_VALUE}>{def.noneLabel} ({facet?.none_count ?? 0})</MenuItem>}
            </TextField>
          );
        })}
        {filtersActive && <Button size="small" onClick={() => setSelected({})}>Clear filters</Button>}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {report && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <SummaryTile
            label="Services"
            value={total.toLocaleString()}
            sub={Object.entries(report.totals.by_type).map(([t, n]) => `${n} ${typeLabel(t)}`).join(' · ') || undefined}
          />
          {report.groups.map((g) => (
            <SummaryTile
              key={g.key}
              label={g.label}
              value={g.count.toLocaleString()}
              sub={Object.entries(g.by_type).map(([t, n]) => `${n} ${typeLabel(t)}`).join(' · ')}
              active={selected[groupFacet] === g.key}
              onClick={() => setSelected((cur) => ({ ...cur, [groupFacet]: cur[groupFacet] === g.key ? '' : g.key }))}
            />
          ))}
        </Box>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Technology</TableCell>
                <TableCell>{groupBy === 'state' ? 'POP / POI' : 'State'}</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !report && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {report && total === 0 && !loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No services match these filters.</TableCell></TableRow>
              )}
              {report && report.groups.map((g) => (
                <React.Fragment key={g.key}>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 }}>
                      {g.label} <Typography component="span" variant="caption" sx={{ color: 'inherit', opacity: 0.85 }}>({g.count} service{g.count !== 1 ? 's' : ''})</Typography>
                    </TableCell>
                  </TableRow>
                  {g.rows.map(renderRow)}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default InternetServicesReportPage;
